import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, ZoomIn, ZoomOut, Download, Check, X, Plus, Trash2, FileSpreadsheet, Wand2, ListOrdered, RotateCw, Eye, EyeOff, FileText, RefreshCw, Share2, Contrast, Ban, Ruler, GripVertical, Settings as SettingsIcon, ChevronDown, Table, Grid3x3, RotateCcw, AlertTriangle, Cpu } from "lucide-react";

const DESIG_TYPES = ["None", "Minor", "Major", "Critical"];   // dropdown order (None clears)
const DESIG_COLOR = {                                          // balloon fill per level
  Minor: "bg-amber-500 ring-amber-200",
  Major: "bg-purple-600 ring-purple-200",
  Critical: "bg-red-600 ring-red-200",
};
const DESIG_BADGE = {                                          // side-list badge per level
  Minor: "text-amber-700 bg-amber-50 border-amber-200",
  Major: "text-purple-700 bg-purple-50 border-purple-200",
  Critical: "text-red-700 bg-red-50 border-red-200",
};
import { toast } from "sonner";
import api from "@/lib/api";

const KIND_COLOR = {
  dimension: "bg-blue-600", tolerance: "bg-amber-500", gdt: "bg-purple-600", surface: "bg-teal-600", note: "bg-slate-500",
};

function ToolBtn({ active, disabled, onClick, title, children }) {
  return (
    <button onClick={onClick} disabled={disabled} title={title}
      className={`p-2 rounded-lg transition disabled:opacity-40 ${active ? "bg-indigo-500 text-white" : "text-slate-200 hover:bg-white/10"}`}>
      {children}
    </button>
  );
}

// Dimension-type catalogue for the Details editor (label, description, mapped kind).
const DIM_TYPES = [
  { cat: "COMMON DIMENSIONS", items: [
    ["Linear", "Basic linear measurement", "dimension"],
    ["Length", "Length measurement", "dimension"],
    ["Hole Depth", "Depth of a hole feature", "dimension"],
    ["Groove Width", "Width of a groove feature", "dimension"],
    ["Spot Face Depth", "Depth of a spot face feature", "dimension"],
    ["Depth (↧)", "Depth callout, e.g. ↧ 10 ±0.5", "dimension"],
    ["Diameter (Ø)", "Circular diameter measurement", "dimension"],
    ["C'bore (⌴)", "Counterbore diameter, e.g. ⌴ Ø120 ±0.5", "dimension"],
    ["C'sink (⌵)", "Countersink diameter, e.g. ⌵ Ø60 ±0.2", "dimension"],
    ["Radius (R)", "Circular radius measurement", "dimension"],
    ["Angular (°)", "Angular measurement in degrees", "dimension"],
    ["Thickness (THK)", "Material thickness measurement", "dimension"],
  ] },
  { cat: "ADVANCED DIMENSIONS", items: [
    ["Custom", "Custom dimension with direct min/max", "dimension"],
    ["GD&T", "Geometric dimensioning and tolerancing", "gdt"],
    ["Straightness (⏤)", "Form tolerance for line element straightness", "gdt"],
    ["Flatness (⏥)", "Form tolerance for surface flatness", "gdt"],
    ["Circularity (○)", "Form tolerance for circular cross-section roundness", "gdt"],
    ["Cylindricity (⌭)", "Form tolerance for cylindrical surface", "gdt"],
    ["Perpendicularity (⊥)", "Orientation tolerance for 90° relationship to datum", "gdt"],
    ["Angularity (∠)", "Orientation tolerance for angular relationship to datum", "gdt"],
    ["Parallelism (∥)", "Orientation tolerance for parallel relationship to datum", "gdt"],
    ["Position (⌖)", "Location tolerance for true position of feature", "gdt"],
    ["Concentricity (◎)", "Location tolerance for coaxial features", "gdt"],
    ["Symmetry (⌯)", "Location tolerance for symmetrical features", "gdt"],
    ["Line Profile (⌒)", "Profile tolerance for 2D line elements", "gdt"],
    ["Surface Profile (⌓)", "Profile tolerance for 3D surface elements", "gdt"],
    ["Circular Runout (↗)", "Runout tolerance for single circular element", "gdt"],
    ["Total Runout (↗↗)", "Runout tolerance for entire surface", "gdt"],
    ["Note", "Text note or comment in the drawing", "note"],
    ["Direct Limits", "Dimension specified as min and max bounds ex: 10.0 to 12.0", "tolerance"],
    ["Bore Diameter (Ø)", "Internal diameter of hole or cylinder ex: Ø25 H7", "dimension"],
    ["Dowel Diameter (Ø)", "External diameter of shaft or pin ex: Ø5m6", "dimension"],
    ["Drill Diameter (Ø)", "Diameter of a drilled hole", "dimension"],
    ["Bolt Center Diameter (Ø)", "Diameter through bolt-hole centers (BCD)", "dimension"],
    ["Groove Diameter (Ø)", "Diameter at the bottom of a groove", "dimension"],
    ["Spot Face Diameter (Ø)", "Diameter of a spot-faced surface", "dimension"],
    ["Pitch Circle Diameter (PCD)", "Diameter of the imaginary circle through evenly-spaced hole centers (PCD)", "dimension"],
    ["Inner Radius (R)", "Radius of internal features like fillets", "dimension"],
    ["Outer Radius (R)", "Radius of external features", "dimension"],
    ["Hole Diameter (Ø)", "Diameter with explicit tolerances ex: Ø8.5 +0.2/-0.1", "dimension"],
    ["Inner Diameter", "Diameter of internal features, like holes", "dimension"],
    ["Outer Diameter", "Diameter of external features, like shafts", "dimension"],
    ["Hole", "Countersunk or counterbore hole", "dimension"],
    ["Thread", "Metric or inch thread ex: M10x1.5-6H × 15 DEEP or 3/8-16 UNC-2B", "dimension"],
    ["Tapped Hole", "A threaded hole feature", "dimension"],
    ["Drill Depth (↧)", "depth of drilling for taps", "dimension"],
    ["Tap Depth", "Separate tap and drill depth specifications", "dimension"],
    ["Chamfer", "Chamfer with length and angle or equal legs ex: 2 × 45° or 2 × 2", "dimension"],
    ["Slot", "Linear cut with depth ex: 15 ↧ 0.5", "dimension"],
    ["Centre Distance (C-C)", "Centre-to-centre distance between features ex: 50 C-C", "dimension"],
    ["Surface Finish", "Surface roughness with optional lay symbol ex: Ra 1.6 ⊥", "surface"],
    ["Fillet weld", "A fillet welding specification with arrow", "dimension"],
    ["Table Coordinates", "Row from a hole/coordinate table with identified columns", "dimension"],
    ["Table Entry", "Row from a table stored as a single string", "dimension"],
  ] },
];
const DIM_KIND = Object.fromEntries(DIM_TYPES.flatMap((g) => g.items).map(([label, , kind]) => [label, kind]));

// Quick GD&T symbol picker — click to set the callout's symbol/type in one tap.
const GDT_QUICK = [
  ["⏤", "Straightness (⏤)"], ["⏥", "Flatness (⏥)"], ["○", "Circularity (○)"], ["⌭", "Cylindricity (⌭)"],
  ["⊥", "Perpendicularity (⊥)"], ["∠", "Angularity (∠)"], ["∥", "Parallelism (∥)"],
  ["⌖", "Position (⌖)"], ["◎", "Concentricity (◎)"], ["⌯", "Symmetry (⌯)"],
  ["⌒", "Line Profile (⌒)"], ["⌓", "Surface Profile (⌓)"], ["↗", "Circular Runout (↗)"], ["↗↗", "Total Runout (↗↗)"],
];

const MODES = [
  { id: "ballooning", label: "Ballooning", icon: <span className="inline-block w-3.5 h-3.5 rounded-full border-2 border-slate-700" /> },
  { id: "titleblock", label: "Title Block", icon: <Table size={15} /> },
  { id: "refgrid", label: "Reference Grid", icon: <Grid3x3 size={15} /> },
  { id: "dimgroups", label: "Dimension Groups", icon: <Share2 size={15} /> },
];

// Balloon palette + per-shape CSS (box shape + counter-rotation for the number).
const PALETTE = ["#1e293b", "#2563eb", "#16a34a", "#dc2626", "#7c3aed", "#f59e0b"];
const SHAPE_STYLE = {
  circle: { box: { borderRadius: "9999px" }, num: {} },
  pin: { box: { borderRadius: "50% 50% 50% 0", transform: "rotate(45deg)" }, num: { transform: "rotate(-45deg)" } },
  square: { box: { borderRadius: "3px" }, num: {} },
  diamond: { box: { borderRadius: "3px", transform: "rotate(45deg)" }, num: { transform: "rotate(-45deg)" } },
  triangle: { box: { clipPath: "polygon(50% 6%, 4% 96%, 96% 96%)" }, num: { transform: "translateY(22%)" } },
};
const LEVEL_SHAPE = { minor: "diamond", major: "triangle", critical: "square" };   // fixed shapes per level
const DEFAULT_LEVELS = {
  regular: { shape: "circle", color: "#2563eb" },
  minor: { color: "#f59e0b" },
  major: { color: "#7c3aed" },
  critical: { color: "#dc2626" },
};

// Pull the GD&T symbol out of a dim_type label like "Surface Profile (⌓)".
function gdtSymbol(b) {
  const m = (b?.dim_type || "").match(/\(([^)]+)\)/);
  return m ? m[1] : "";
}
// Does the callout text look like a GD&T feature-control frame?
function looksGdt(t) {
  const s = t || "";
  return /\b[A-D]\b[\s|]+\b[A-D]\b/.test(s) || /^[Ø⌀]?\d?\.\d+\s+[A-D]\b/.test(s);
}
// Best-effort geometric symbol from the text (profile/surface callouts).
function guessGdtSymbol(t) {
  const s = (t || "").toUpperCase();
  if (/SHADED|PROFILE|\bSURF/.test(s)) return "⌓";   // profile of a surface
  return "▱";                                         // generic GD&T marker until the type is set
}
// Render a GD&T callout as a feature-control frame: [symbol | value | A | B | C].
function GdtFrame({ symbol, text }) {
  const t = (text || "").trim();
  const m = t.match(/[Ø⌀]?\d+\.\d+.*$/);   // keep from the tolerance value on (drop leading prose)
  const cells = (m ? m[0] : t).split(/\s+/).filter(Boolean);
  return (
    <span className="inline-flex items-stretch border border-slate-700 rounded-[3px] text-xs font-mono leading-none bg-white">
      {symbol ? <span className="px-1.5 py-1 border-r border-slate-700 flex items-center text-sm">{symbol}</span> : null}
      {cells.map((c, i) => (
        <span key={i} className="px-1.5 py-1 border-r border-slate-700 last:border-r-0 flex items-center">{c}</span>
      ))}
    </span>
  );
}

function TypeDropdown({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ql = q.trim().toLowerCase();
  return (
    <div className="relative">
      <button onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between border border-slate-300 rounded-md px-3 py-2 text-sm bg-white">
        <span className="truncate">{value || "Custom"}</span>
        <span className="text-slate-400">▾</span>
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-xl" onMouseDown={(e) => e.stopPropagation()}>
          <div className="p-2 border-b border-slate-100">
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…"
              className="w-full border-2 border-orange-400 rounded px-2 py-1.5 text-sm outline-none" />
          </div>
          <div className="max-h-72 overflow-y-auto">
            {DIM_TYPES.map((g) => {
              const items = g.items.filter(([l, d]) => !ql || l.toLowerCase().includes(ql) || d.toLowerCase().includes(ql));
              if (!items.length) return null;
              return (
                <div key={g.cat}>
                  <div className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400 bg-slate-50 sticky top-0">{g.cat}</div>
                  {items.map(([label, desc, kind]) => (
                    <button key={label} onClick={() => { onChange(label, kind); setOpen(false); setQ(""); }}
                      className={`block w-full text-left px-3 py-2 hover:bg-slate-50 ${value === label ? "bg-orange-50" : ""}`}>
                      <div className={`text-sm font-medium ${value === label ? "text-orange-600" : "text-slate-800"}`}>{label}</div>
                      <div className="text-[11px] text-slate-500 leading-tight">{desc}</div>
                    </button>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default function BallooningViewer() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const balloons = doc?.balloons || [];
  const page = (doc?.pages || [])[0];   // MVP: first page
  // Balloon circle sits OFFSET from its dimension (anchor x,y) so the number never covers the value.
  // ax/ay = where the user dragged the circle; otherwise auto-lift it just above the callout.
  const LIFT = 0.03;
  const circlePos = (b) => (b.ax != null ? { x: b.ax, y: b.ay } : { x: b.x, y: Math.max(0.015, b.y - LIFT) });
  const [loading, setLoading] = useState(true);
  const [zoom, setZoom] = useState(0.6);
  const [tab, setTab] = useState("dims");   // dims | tols
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const imgWrapRef = useRef(null);
  const colRef = useRef(null);        // drawing column (drag bounds for the tool dock)
  const barRef = useRef(null);        // the tool dock element
  const toolDrag = useRef(null);      // active drag offset
  const [toolPos, setToolPos] = useState(null);   // {x,y} px within column; null = default bottom-centre
  function onGripDown(e) {
    e.preventDefault(); e.stopPropagation();
    const bar = barRef.current.getBoundingClientRect();
    toolDrag.current = { dx: e.clientX - bar.left, dy: e.clientY - bar.top };
  }
  useEffect(() => {
    const move = (e) => {
      const d = toolDrag.current; if (!d || !colRef.current) return;
      const col = colRef.current.getBoundingClientRect();
      setToolPos({ x: e.clientX - col.left - d.dx, y: e.clientY - col.top - d.dy });
    };
    const up = () => { toolDrag.current = null; };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    return () => { window.removeEventListener("mousemove", move); window.removeEventListener("mouseup", up); };
  }, []);
  // Hide numbers (H) — hides the numbered balloons but KEEPS an orange mark on every detected
  // dimension, so any callout without a mark is one still left to balloon.
  const [showNumbers, setShowNumbers] = useState(true);
  const [showBoxes, setShowBoxes] = useState(false);   // orange (auto-detected) boxes — toggled by "B"
  const [rotation, setRotation] = useState(0);         // manual view rotation (0/90/180/270)
  const [greenMode, setGreenMode] = useState(false);   // add-box mode — drag to add a new (green) box
  const anyBoxMode = showBoxes || greenMode;
  async function addGreenBox(r) {
    // Green box = a dimension the OCR missed; user marks it (OCR the region, may be empty → they type).
    const cx = r.x + r.w / 2, cy = r.y + r.h / 2;
    const text = await readAt(cx, cy, r.w, r.h);
    const n = balloons.length + 1;
    updateBalloons([...balloons, { n, page: page?.page || 1, x: Number(cx.toFixed(4)), y: Number(cy.toFixed(4)), text, kind: looksGdt(text) ? "gdt" : "dimension", bw: Number(r.w.toFixed(4)), bh: Number(r.h.toFixed(4)), manual: true, verified: false }]);
    toast.success("Green box added" + (text ? `: ${text.slice(0, 24)}` : " — type the value"));
  }
  async function saveAndSequence() {
    // Persist orange + green boxes and number everything clockwise from 1, then show the numbers.
    await renumberClockwise(balloons, 1);
    setShowBoxes(false); setGreenMode(false); setShowNumbers(true);
    toast.success("Saved & sequenced clockwise from 1");
  }
  function cycleView() {
    setShowNumbers((s) => {
      const next = !s;
      toast(next ? "Balloon numbers shown" : "Numbers hidden — un-marked callouts are still left to balloon");
      return next;
    });
  }
  // ---- EYE mode (shared by Ballooning + KC): hide balloons, white-mask every detected item,
  // and let the user box any STILL-VISIBLE (missed) text to OCR + add it. The mask lives in the
  // same fractional-coord stage as the balloons, so it follows zoom / pan / rotation for free.
  const [eyeMode, setEyeMode] = useState(false);
  const [eyePending, setEyePending] = useState([]);   // {id, bbox:{x,y,w,h}, text, parsed, busy, error}
  const isKcDoc = doc?.source === "kc";
  function toggleEye() { eyeMode ? cancelEye() : startEye(); }
  function startEye() { setEyePending([]); setDetailFor(null); setEyeMode(true);
    toast("Detected items hidden — box any text still showing to read it (Esc to cancel)"); }
  function cancelEye() { setEyeMode(false); setEyePending([]); }
  async function readRegion(r) {
    const pid = `pe_${Math.round(r.x * 1e4)}_${Math.round(r.y * 1e4)}_${eyePending.length}`;
    setEyePending((prev) => [...prev, { id: pid, bbox: r, text: "", parsed: null, busy: true }]);
    try {
      const { data } = await api.post(`/kc/projects/${id}/read-region`, {
        bbox: [r.x, r.y, r.x + r.w, r.y + r.h], angle: "auto",
      });
      setEyePending((prev) => prev.map((p) => p.id === pid
        ? { ...p, text: data?.text || "", parsed: data?.parsed || null, conf: data?.conf, busy: false } : p));
    } catch (e) {
      setEyePending((prev) => prev.map((p) => p.id === pid
        ? { ...p, busy: false, error: e?.response?.data?.detail || "read failed — type the value" } : p));
    }
  }
  async function finishEye() {
    const items = eyePending.filter((p) => (p.text || "").trim());
    if (items.length) {
      const next = balloons.slice();
      let n = next.length;
      for (const it of items) {
        n += 1;
        const cx = it.bbox.x + it.bbox.w / 2, cy = it.bbox.y + it.bbox.h / 2;
        next.push({
          n, page: page?.page || 1,
          x: Number(cx.toFixed(4)), y: Number(cy.toFixed(4)),
          ax: Number(cx.toFixed(4)), ay: Number(cy.toFixed(4)),
          bw: Number(it.bbox.w.toFixed(4)), bh: Number(it.bbox.h.toFixed(4)),
          text: it.text.trim(), kind: looksGdt(it.text) ? "gdt" : "dimension",
          manual: true, verified: false, source: "manual-ocr",
        });
      }
      try {
        await api.put(`/ballooning/${id}/balloons`, { balloons: next });
        updateBalloons(next); setDirty(false);
        toast.success(`${items.length} dimension(s) added`);
      } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); return; }
    }
    setEyeMode(false); setEyePending([]);
  }
  const [addNoteMode, setAddNoteMode] = useState(false);     // Add Note (N): next click drops a note balloon
  // Settings (persisted) + the corner mode dropdown.
  const [settings, setSettings] = useState(() => {
    const def = { size: 1, startNumber: 1, autoRenumber: true, direction: "cw", levels: DEFAULT_LEVELS };
    try {
      const st = JSON.parse(localStorage.getItem("bln_settings") || "{}");
      return { ...def, ...st, levels: { ...DEFAULT_LEVELS, ...(st.levels || {}) } };
    } catch { return def; }
  });
  useEffect(() => { try { localStorage.setItem("bln_settings", JSON.stringify(settings)); } catch { /* ignore */ } }, [settings]);
  const setSetting = (k, v) => setSettings((s) => ({ ...s, [k]: v }));
  const setLevel = (lvl, patch) => setSettings((s) => ({ ...s, levels: { ...s.levels, [lvl]: { ...s.levels[lvl], ...patch } } }));
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsTab, setSettingsTab] = useState("balloons");
  const [mode, setMode] = useState("ballooning");
  const [modeOpen, setModeOpen] = useState(false);
  const [renumbering, setRenumbering] = useState(false);
  // Re-order dimensions (O): click balloons one-by-one in the exact sequence you want.
  const [seqMode, setSeqMode] = useState(false);
  const [seqOrder, setSeqOrder] = useState([]);              // balloon n's in click order
  const [seqApplying, setSeqApplying] = useState(false);
  function pickSeq(n) { setSeqOrder((o) => (o.includes(n) ? o.filter((x) => x !== n) : [...o, n])); }
  // Add Ch. Designator Symbol: drag a box over an area → mark the dimensions inside as
  // Critical / Major / Minor / Significant characteristics.
  const [desigMode, setDesigMode] = useState(false);
  const [dragStart, setDragStart] = useState(null);   // {x,y} frac
  const [dragRect, setDragRect] = useState(null);     // {x,y,w,h} frac
  const [desig, setDesig] = useState(null);           // {rect, ids:[n], type} — active popup
  function fracFromEvent(e) {
    const r = imgWrapRef.current.getBoundingClientRect();
    return { x: (e.clientX - r.left) / r.width, y: (e.clientY - r.top) / r.height };
  }
  function stagePlainMode() {
    return !seqMode && !desigMode && !redactMode && !regionMode && !reorderMode && !addNoteMode && !addDimMode && !eyeMode;
  }
  function onStageMouseDown(e) {
    if (!eyeMode && !desigMode && !redactMode && !regionMode && !stagePlainMode()) return;
    const p = fracFromEvent(e);
    setDesig(null); setDragStart(p); setDragRect({ x: p.x, y: p.y, w: 0, h: 0 });
  }
  function onStageMouseMove(e) {
    if (!dragStart) return;
    const p = fracFromEvent(e);
    setDragRect({ x: Math.min(dragStart.x, p.x), y: Math.min(dragStart.y, p.y), w: Math.abs(p.x - dragStart.x), h: Math.abs(p.y - dragStart.y) });
  }
  function onStageMouseUp() {
    if (!dragStart) return;
    const r = dragRect; setDragStart(null);
    // Tiny movement = a click → let onImageClick add a numbered balloon.
    if (!r || r.w < 0.012 || r.h < 0.012) { setDragRect(null); return; }
    if (eyeMode) { readRegion(r); setDragRect(null); return; }   // EYE: OCR the boxed missed text
    if (regionMode) { addRegion(r, regionMode); setDragRect(null); return; }
    if (redactMode) { addRedaction(r); setDragRect(null); return; }
    if (stagePlainMode()) {
      suppressClick.current = true;
      if (greenMode) addGreenBox(r);          // Green = add a new box
      else if (showBoxes) { /* Show = view/edit only — don't create on drag */ }
      else addBoxedBalloon(r);                // normal mode: drag-a-box still works
      setDragRect(null); return;
    }
    const m = 0.008;  // small margin so balloons right at the edge still count
    const ids = balloons.filter((b) => b.x >= r.x - m && b.x <= r.x + r.w + m && b.y >= r.y - m && b.y <= r.y + r.h + m).map((b) => b.n);
    if (!ids.length) { setDragRect(null); toast("No dimensions inside the box"); return; }
    applyDesig(ids, "Critical", true);
    setDesig({ rect: r, ids, type: "Critical" });
  }
  // Redact an area (R): drag a box → black-out that region (also blacked out in the exported PDF).
  const [redactMode, setRedactMode] = useState(false);
  const redactions = doc?.redactions || [];
  async function saveRedactions(list) {
    setDoc((d) => ({ ...d, redactions: list }));
    try { await api.put(`/ballooning/${id}/redactions`, { redactions: list }); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save redaction"); }
  }
  function addRedaction(r) {
    saveRedactions([...(doc?.redactions || []), { page: page?.page || 1, x: r.x, y: r.y, w: r.w, h: r.h }]);
    toast.success("Area redacted");
  }
  function removeRedaction(idx) { saveRedactions((doc?.redactions || []).filter((_, i) => i !== idx)); }

  // Views / tables — rectangular regions drawn on the drawing.
  const views = doc?.views || [];
  const [regionMode, setRegionMode] = useState(null);      // "view" | "table" | null
  const [selectedView, setSelectedView] = useState(null);
  async function saveViews(list) {
    setDoc((d) => ({ ...d, views: list }));
    try { await api.put(`/ballooning/${id}/views`, { views: list }); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save view"); }
  }
  function addRegion(r, kind) {
    const count = (doc?.views || []).filter((v) => (v.kind || "view") === kind).length;
    const name = (kind === "table" ? "Table #" : "View #") + (count + 1);
    const v = { id: Math.random().toString(36).slice(2, 10), name, kind, page: page?.page || 1, x: r.x, y: r.y, w: r.w, h: r.h };
    saveViews([...(doc?.views || []), v]);
    setSelectedView(v.id); setRegionMode(null);
    toast.success(`${name} added`);
  }
  function removeView(vid) { saveViews((doc?.views || []).filter((v) => v.id !== vid)); if (selectedView === vid) setSelectedView(null); }
  const [numbering, setNumbering] = useState(false);
  async function numberByViews() {
    setNumbering(true);
    try {
      if (dirty) await api.put(`/ballooning/${id}/balloons`, { balloons });   // save edits first
      await api.post(`/ballooning/${id}/number-by-views`);
      await load();
      setShowAllViews(false); setSelectedView(null); setTab("dims");   // hide the view boxes, show the balloons
      toast.success("Numbered by views");
    } catch (e) { toast.error(e?.response?.data?.detail || "Number by views failed"); }
    finally { setNumbering(false); }
  }
  const [showAllViews, setShowAllViews] = useState(true);
  // Drag / resize a view region.
  const viewsRef = useRef([]);
  useEffect(() => { viewsRef.current = doc?.views || []; }, [doc?.views]);
  const viewDrag = useRef(null);
  function onViewMouseDown(e, v, mode) {
    e.stopPropagation(); setSelectedView(v.id);
    const r = imgWrapRef.current?.getBoundingClientRect();
    viewDrag.current = { id: v.id, mode, sx: e.clientX, sy: e.clientY, x: v.x, y: v.y, w: v.w, h: v.h, rw: r?.width || 1, rh: r?.height || 1, moved: false };
  }
  useEffect(() => {
    const cl = (v) => Math.max(0, Math.min(1, v));
    const move = (e) => {
      const d = viewDrag.current; if (!d) return;
      d.moved = true;
      const dx = (e.clientX - d.sx) / d.rw, dy = (e.clientY - d.sy) / d.rh;
      setDoc((prev) => ({
        ...prev,
        views: (prev.views || []).map((vv) => {
          if (vv.id !== d.id) return vv;
          if (d.mode === "move") return { ...vv, x: Number(cl(d.x + dx).toFixed(4)), y: Number(cl(d.y + dy).toFixed(4)) };
          return { ...vv, w: Number(Math.max(0.03, d.w + dx).toFixed(4)), h: Number(Math.max(0.03, d.h + dy).toFixed(4)) };
        }),
      }));
    };
    const up = () => { const d = viewDrag.current; viewDrag.current = null; if (d && d.moved) saveViews(viewsRef.current); };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    return () => { window.removeEventListener("mousemove", move); window.removeEventListener("mouseup", up); };
    // eslint-disable-next-line
  }, []);

  // Detection-box (hide-numbers mode) — move / resize / delete.
  const autoBoxW = (b) => Math.min(0.16, Math.max(0.03, (b.text || "").length * 0.0065));
  const boxDrag = useRef(null);
  const balloonsRef = useRef([]);
  useEffect(() => { balloonsRef.current = doc?.balloons || []; }, [doc?.balloons]);
  async function rereadBox(n) {
    // Re-OCR a box's current region and put the read value into its dimension (list) text.
    const b = balloonsRef.current.find((x) => x.n === n);
    if (!b) return;
    const t = await readAt(b.x, b.y, b.bw || autoBoxW(b), b.bh || 0.022);
    if (t) setDoc((prev) => ({ ...prev, balloons: (prev.balloons || []).map((x) => x.n === n ? { ...x, text: t, kind: looksGdt(t) ? "gdt" : x.kind } : x) }));
  }
  function onBoxMouseDown(e, b, mode) {
    e.stopPropagation();
    const r = imgWrapRef.current?.getBoundingClientRect();
    boxDrag.current = { n: b.n, mode, sx: e.clientX, sy: e.clientY, startX: b.x, startY: b.y, startW: b.bw || autoBoxW(b), startH: b.bh || 0.022, rw: r?.width || 1, rh: r?.height || 1, moved: false };
  }
  useEffect(() => {
    const cl = (v) => Math.max(0, Math.min(1, v));
    const move = (e) => {
      const d = boxDrag.current; if (!d) return;
      d.moved = true;
      const dx = (e.clientX - d.sx) / d.rw, dy = (e.clientY - d.sy) / d.rh;
      setDoc((prev) => ({
        ...prev,
        balloons: (prev.balloons || []).map((bb) => {
          if (bb.n !== d.n) return bb;
          if (d.mode === "move") return { ...bb, x: Number(cl(d.startX + dx).toFixed(4)), y: Number(cl(d.startY + dy).toFixed(4)) };
          return { ...bb, bw: Number(Math.max(0.02, d.startW + dx).toFixed(4)), bh: Number(Math.max(0.012, d.startH + dy).toFixed(4)) };
        }),
      }));
      setDirty(true);
    };
    const up = () => { const d = boxDrag.current; boxDrag.current = null; if (d && d.moved) rereadBox(d.n); };   // re-read value from the adjusted box
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    return () => { window.removeEventListener("mousemove", move); window.removeEventListener("mouseup", up); };
    // eslint-disable-next-line
  }, []);

  // Details editor (pre6-style): click a balloon to edit its type + values.
  const [detailFor, setDetailFor] = useState(null);   // balloon n being edited
  const detailBalloon = balloons.find((b) => b.n === detailFor) || null;
  function composeText(b) {
    if ((b.dim_type || "") === "Note") return b.note || "";
    const base = b.base || "";
    let t = b.basic && base ? `[${base}]` : base;
    const plus = b.tmax, minus = b.tmin;
    if (plus || minus) t = `${t} +${plus || 0}/-${minus || 0}`.trim();
    if (b.dclass) t = `${t} ${b.dclass}`.trim();
    if (b.count && b.count !== "1") t = `${b.count}X ${t}`.trim();
    return t.trim();
  }
  function patchDetail(patch) {
    if (detailFor == null) return;
    updateBalloons(balloons.map((b) => {
      if (b.n !== detailFor) return b;
      const nb = { ...b, ...patch };
      const composed = composeText(nb);
      if (composed) nb.text = composed;                 // keep the visible callout in sync
      else if ((nb.dim_type || "") === "Note") nb.text = "";
      return nb;
    }));
  }
  function changeType(label, kind) {
    const b = detailBalloon; if (!b) return;
    const patch = { dim_type: label, kind };
    if (label === "Note" && !b.note) patch.note = b.text || "";
    else if (label !== "Note" && !b.base) patch.base = b.text || "";
    patchDetail(patch);
  }

  // Drag a balloon to reposition it (leader line stretches back to the dimension point).
  const balloonDrag = useRef(null);
  function onBalloonMouseDown(e, b, end = "circle") {
    if (seqMode || desigMode || redactMode || reorderMode) return;   // those modes use clicks/drags differently
    e.stopPropagation();
    // Capture where inside the handle we grabbed, so it follows the cursor without jumping.
    const p = end === "anchor" ? { x: b.x, y: b.y } : circlePos(b);
    const r = imgWrapRef.current?.getBoundingClientRect();
    const gx = r ? e.clientX - (r.left + p.x * r.width) : 0;
    const gy = r ? e.clientY - (r.top + p.y * r.height) : 0;
    balloonDrag.current = {
      n: b.n, end, sx: e.clientX, sy: e.clientY, gx, gy, moved: false,
      startAnchor: { x: b.x, y: b.y }, startCircle: circlePos(b),
    };
  }
  useEffect(() => {
    const clamp = (v) => Math.max(0, Math.min(1, v));
    const move = (e) => {
      const d = balloonDrag.current; if (!d || !imgWrapRef.current) return;
      if (!d.moved && Math.hypot(e.clientX - d.sx, e.clientY - d.sy) < 4) return;   // ignore jitter
      d.moved = true;
      const r = imgWrapRef.current.getBoundingClientRect();
      const x = clamp((e.clientX - (d.gx || 0) - r.left) / r.width), y = clamp((e.clientY - (d.gy || 0) - r.top) / r.height);
      setDoc((prev) => ({
        ...prev,
        balloons: (prev.balloons || []).map((bb) => {
          if (bb.n !== d.n) return bb;
          const nx = Number(x.toFixed(4)), ny = Number(y.toFixed(4));
          if (d.end === "anchor") {
            // Move only the leader tip; freeze the circle where it is.
            const freeze = bb.ax != null ? {} : { ax: bb.x, ay: Math.max(0.015, bb.y - LIFT) };
            return { ...bb, ...freeze, x: nx, y: ny };
          }
          // Move the circle AND carry the leader tip by the same delta (rigid move).
          const dx = nx - d.startCircle.x, dy = ny - d.startCircle.y;
          const ax2 = Math.min(1, Math.max(0, d.startAnchor.x + dx));
          const ay2 = Math.min(1, Math.max(0, d.startAnchor.y + dy));
          return { ...bb, ax: nx, ay: ny, x: Number(ax2.toFixed(4)), y: Number(ay2.toFixed(4)) };
        }),
      }));
      setDirty(true);
    };
    const up = () => {
      const d = balloonDrag.current; if (!d) return;
      balloonDrag.current = null;
      if (!d.moved) setDetailFor(d.n);   // it was a click, not a drag → open Details
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    return () => { window.removeEventListener("mousemove", move); window.removeEventListener("mouseup", up); };
  }, []);
  function applyDesig(ids, type, announce) {
    const set = new Set(ids);
    updateBalloons(balloons.map((b) => (set.has(b.n) ? { ...b, designator: type } : b)));
    if (announce) toast.success(`Marked ${ids.length} dimension${ids.length > 1 ? "s" : ""} as ${type}`);
  }
  function changeDesig(type) {
    if (!desig) return;
    const value = type === "None" ? "" : type;   // None clears the designator
    applyDesig(desig.ids, value, false);
    setDesig({ ...desig, type });
  }
  function clearDesig() {
    if (!desig) return;
    const set = new Set(desig.ids);
    updateBalloons(balloons.map((b) => (set.has(b.n) ? { ...b, designator: "" } : b)));
    setDesig(null); setDragRect(null);
  }
  async function applySequence() {
    const chosen = seqOrder.map((n) => balloons.find((b) => b.n === n)).filter(Boolean);
    const chosenSet = new Set(seqOrder);
    const rest = balloons.filter((b) => !chosenSet.has(b.n)).sort((a, b) => a.n - b.n);
    const ordered = [...chosen, ...rest].map((b, i) => ({ ...b, n: i + (settings.startNumber || 1) }));
    setSeqApplying(true);
    try {
      await api.put(`/ballooning/${id}/balloons`, { balloons: ordered });
      await load();
      toast.success("Dimensions re-ordered");
      setSeqMode(false); setSeqOrder([]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Re-order failed");
    } finally { setSeqApplying(false); }
  }

  async function load() {
    setLoading(true);
    try {
      const r = await api.get(`/ballooning/${id}`);
      setDoc(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load drawing");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  function updateBalloons(next) { setDoc((d) => ({ ...d, balloons: next })); setDirty(true); }
  function setText(n, text) { updateBalloons(balloons.map((b) => (b.n === n ? { ...b, text } : b))); }
  function toggleVerify(n) { updateBalloons(balloons.map((b) => (b.n === n ? { ...b, verified: !b.verified } : b))); }
  function removeBalloon(n) {
    const next = balloons.filter((b) => b.n !== n).map((b, i) => ({ ...b, n: i + 1 }));
    updateBalloons(next);
  }
  // Drag a dimension in the list to a new number position → renumber to match.
  const [dragIdx, setDragIdx] = useState(null);
  function reorderDims(from, to) {
    if (from == null || to == null || from === to) return;
    const arr = [...balloons].sort((a, b) => (a.n || 0) - (b.n || 0));
    const [moved] = arr.splice(from, 1);
    arr.splice(to, 0, moved);
    const start = settings.startNumber || 1;
    updateBalloons(arr.map((b, i) => ({ ...b, n: i + start })));
  }
  // Change a balloon's number → move it to that position; everything after re-sequences.
  const [numDraft, setNumDraft] = useState(null);
  useEffect(() => { setNumDraft(null); }, [detailFor]);
  function setBalloonNumber(curN, newN) {
    const start = settings.startNumber || 1;
    const arr = [...balloons].sort((a, b) => (a.n || 0) - (b.n || 0));
    const from = arr.findIndex((b) => b.n === curN);
    const to = Math.max(0, Math.min(arr.length - 1, (newN - start)));
    if (from < 0 || from === to) return;
    const [moved] = arr.splice(from, 1);
    arr.splice(to, 0, moved);
    updateBalloons(arr.map((b, i) => ({ ...b, n: i + start })));
    setDetailFor(to + start);   // keep the panel on the moved balloon (now at its new number)
  }
  function commitNumDraft() {
    if (numDraft == null || !detailBalloon) return;
    const v = parseInt(numDraft, 10);
    if (v && v !== detailBalloon.n) setBalloonNumber(detailBalloon.n, v);
    setNumDraft(null);
  }
  async function readAtFull(xFrac, yFrac, w, h) {
    // Returns the parsed characteristic ({text,type,tolerance,datums,gdt_symbol,base,tmin,tmax,...}).
    try {
      const r = await api.post(`/ballooning/${id}/read-at`, { page: page?.page || 1, x: xFrac, y: yFrac, w: w || 0, h: h || 0 });
      return r.data || {};
    } catch { return {}; }
  }
  async function readAt(xFrac, yFrac, w, h) {
    const r = await readAtFull(xFrac, yFrac, w, h);
    return (r?.text || "").trim();
  }
  const suppressClick = useRef(false);   // set after a box-drag so the trailing click doesn't also add a balloon
  async function addBoxedBalloon(r) {
    // Drag over text → analyze the whole box (engineering parser) and create a structured callout.
    const cx = r.x + r.w / 2, cy = r.y + r.h / 2;
    const res = await readAtFull(cx, cy, r.w, r.h);
    const text = (res?.text || "").trim();
    const k = (res?.type === "GD&T" || looksGdt(text)) ? "gdt"
      : (/[A-Za-z]{4,}/.test(text) && !/[Ø⌀±]/.test(text) ? "note" : "dimension");
    const n = balloons.length + 1;
    const nb = { n, page: page?.page || 1, x: Number(cx.toFixed(4)), y: Number(cy.toFixed(4)),
      text, kind: k, bw: Number(r.w.toFixed(4)), bh: Number(r.h.toFixed(4)), verified: false };
    // Auto-fill the structured Details fields from the parser (base / ± tol / GD&T).
    if (res?.type) nb.type = res.type;
    if (res?.base) nb.base = res.base;
    if (res?.tmax) nb.tmax = res.tmax;
    if (res?.tmin) nb.tmin = res.tmin;
    if (res?.tolerance) nb.tolerance = res.tolerance;
    if (res?.gdt_symbol) nb.gdt_symbol = res.gdt_symbol;
    if (Array.isArray(res?.datums) && res.datums.length) nb.datums = res.datums;
    if (res?.mc) nb.mc = res.mc;
    updateBalloons([...balloons, nb]);
    toast.success(text ? `Boxed: ${text.slice(0, 30)}` : "Box added — type the value");
  }
  async function addBalloonAt(xFrac, yFrac, kind = "dimension") {
    const text = await readAt(xFrac, yFrac);   // auto-read the value at this spot
    const k = looksGdt(text) ? "gdt" : kind;   // frame it if it reads like a GD&T callout
    const n = balloons.length + 1;
    // Just append as the next number — never auto-reorder existing balloons on add.
    // (Use the ⟳ Auto-number tool or Re-order when you want to renumber.)
    updateBalloons([...balloons, { n, page: page?.page || 1, x: xFrac, y: yFrac, text, kind: k, verified: false }]);
  }
  function addNote(xFrac, yFrac) {
    // Place a Note balloon and open its Details panel so the user types the note text.
    const n = balloons.length + 1;
    updateBalloons([...balloons, { n, page: page?.page || 1, x: xFrac, y: yFrac, text: "", kind: "note", dim_type: "Note", note: "", verified: false }]);
    setAddNoteMode(false);
    setDetailFor(n);
  }
  const [addDimMode, setAddDimMode] = useState(false);   // Add Dimension (A)
  async function addDimension(xFrac, yFrac) {
    // Place a dimension balloon, auto-read the value, and open its Details editor.
    const text = await readAt(xFrac, yFrac);
    const n = balloons.length + 1;
    updateBalloons([...balloons, { n, page: page?.page || 1, x: xFrac, y: yFrac, text, kind: "dimension", dim_type: "Custom", base: text, tmin: "", tmax: "", basic: false, verified: false }]);
    setAddDimMode(false);
    setDetailFor(n);
  }
  function onImageClick(e) {
    if (suppressClick.current) { suppressClick.current = false; return; }   // a box-drag just happened
    if (anyBoxMode) return;   // in box modes, add boxes by dragging (not by click)
    if (!imgWrapRef.current) return;
    const rect = imgWrapRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    if (!(x >= 0 && x <= 1 && y >= 0 && y <= 1)) return;
    if (seqMode || desigMode || redactMode || regionMode) return;   // these modes use balloon-click / drag-box
    if (reorderMode) pickPointAt(x, y);
    else if (addNoteMode) addNote(Number(x.toFixed(4)), Number(y.toFixed(4)));
    else if (addDimMode) addDimension(Number(x.toFixed(4)), Number(y.toFixed(4)));
    else addBalloonAt(Number(x.toFixed(4)), Number(y.toFixed(4)));   // hide-numbers too: just drop a numbered mark, no Details
  }
  async function save() {
    setSaving(true);
    try {
      await api.put(`/ballooning/${id}/balloons`, { balloons });
      setDirty(false);
      toast.success("Saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  }
  function downloadPdf() {
    window.open(`${api.defaults.baseURL}/ballooning/${id}/ballooned.pdf`, "_blank");
  }
  function downloadInspection() {
    window.open(`${api.defaults.baseURL}/ballooning/${id}/inspection.xlsx`, "_blank");
  }
  // Re-order-by-section mode.
  const [reorderMode, setReorderMode] = useState(false);
  const [picked, setPicked] = useState([]);   // clicked section centres in order
  const [applying, setApplying] = useState(false);
  useEffect(() => {
    const onKey = (e) => {
      const t = e.target;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA")) return;  // don't hijack typing
      if (e.key === "Escape") { if (eyeMode) { cancelEye(); } setReorderMode(false); setPicked([]); setAddNoteMode(false); setAddDimMode(false); setSeqMode(false); setSeqOrder([]); setDesigMode(false); setDesig(null); setDragRect(null); setRedactMode(false); setRegionMode(null); setDetailFor(null); return; }
      if ((e.key === "Delete" || e.key === "Backspace") && detailFor != null) {   // fast-delete selected balloon
        e.preventDefault(); removeBalloon(detailFor); setDetailFor(null); return;
      }
      const k = e.key.toLowerCase();
      if (k === "a") { setAddDimMode((m) => !m); setAddNoteMode(false); setReorderMode(false); setSeqMode(false); setDesigMode(false); setRedactMode(false); }
      else if (k === "n") { setAddNoteMode((m) => !m); setAddDimMode(false); setReorderMode(false); setSeqMode(false); setDesigMode(false); setRedactMode(false); }
      else if (k === "v") { setReorderMode((m) => !m); setPicked([]); setAddNoteMode(false); setSeqMode(false); setDesigMode(false); setRedactMode(false); }
      else if (k === "o") { setSeqMode((m) => !m); setSeqOrder([]); setReorderMode(false); setAddNoteMode(false); setDesigMode(false); setRedactMode(false); }
      else if (k === "r") { setRedactMode((m) => !m); setDesigMode(false); setReorderMode(false); setSeqMode(false); setAddNoteMode(false); }
      else if (k === "h") { toggleEye(); }
      else if (k === "b") { setShowBoxes((s) => { toast(s ? "Detection boxes hidden" : "Detection boxes shown"); return !s; }); }
      else if (k === "s") { renumberClockwise(); toast("Re-sequenced (clockwise)"); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line
  }, [reorderMode, addNoteMode, addDimMode, seqMode, desigMode, redactMode, detailFor]);
  function pickPointAt(xFrac, yFrac) {
    setPicked((p) => [...p, { x: Number(xFrac.toFixed(4)), y: Number(yFrac.toFixed(4)) }]);
  }
  function undoPick() { setPicked((p) => p.slice(0, -1)); }
  async function applyReorder() {
    setApplying(true);
    try {
      await api.post(`/ballooning/${id}/reorder`, { order: picked.map((p) => ({ x: p.x, y: p.y })) });
      await load();
      toast.success("Balloons re-ordered");
      setReorderMode(false); setPicked([]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Re-order failed");
    } finally { setApplying(false); }
  }

  // Persist the given balloon list, then have the backend re-number them CLOCKWISE per view.
  async function renumberClockwise(list, startOverride) {
    const bs = list || balloons;
    setRenumbering(true);
    try {
      await api.put(`/ballooning/${id}/balloons`, { balloons: bs });
      await api.post(`/ballooning/${id}/reorder`, { order: [], direction: settings.direction, start: startOverride || settings.startNumber });
      await load();
    } catch (e) {
      // Fall back to keeping the local list if the round-trip fails.
      updateBalloons(bs);
      toast.error(e?.response?.data?.detail || "Re-number failed");
    } finally { setRenumbering(false); }
  }

  const [reprocessing, setReprocessing] = useState(false);
  async function reballoon() {
    if (!window.confirm("Re-run auto-detection? This replaces the current balloons.")) return;
    setReprocessing(true);
    try {
      await api.post(`/ballooning/${id}/reprocess`);
      await load();
      toast.success("Re-ballooned");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Re-balloon failed");
    } finally { setReprocessing(false); }
  }

  const shown = tab === "tols" ? balloons.filter((b) => ["tolerance", "gdt", "surface"].includes(b.kind)) : balloons;
  const verified = balloons.filter((b) => b.verified).length;

  if (loading) return <div className="p-10 text-center text-sm text-slate-400">Loading drawing…</div>;
  if (!doc) return <div className="p-10 text-center text-sm text-slate-400">Not found.</div>;

  return (
    <div className="flex h-[calc(100vh-56px)]" data-testid="ballooning-viewer">
      {/* Drawing viewer */}
      <div ref={colRef} className="flex-1 min-w-0 flex flex-col bg-slate-100 relative">
        <div className="flex items-center justify-between px-4 py-2 bg-white border-b border-slate-200">
          <button onClick={() => navigate("/ballooning")} className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900" data-testid="bln-back">
            <ArrowLeft size={16} /> Home
          </button>
          <div className="font-semibold text-slate-800 truncate px-3 flex items-center gap-2 min-w-0">
            <span className="truncate">{doc.name}</span>
            {isKcDoc && (doc.engine_label || doc.engine_used) && (
              <span title={doc.engine_why || ""} data-testid="bln-kc-engine" className="inline-flex items-center gap-1 shrink-0 text-[10px] font-semibold text-violet-700 bg-violet-50 border border-violet-200 rounded px-1.5 py-0.5">
                <Cpu size={11} /> {doc.engine_label || doc.engine_used}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1">
            <button onClick={reballoon} disabled={reprocessing} className="inline-flex items-center gap-1 px-2 py-1.5 text-xs border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-50 mr-1" title="Re-run auto-detection" data-testid="bln-reballoon">
              <Wand2 size={14} /> {reprocessing ? "…" : "Re-balloon"}
            </button>
            <button onClick={() => setShowBoxes((s) => !s)} className={`inline-flex items-center gap-1 px-2 py-1.5 text-xs rounded mr-1 ${showBoxes ? "bg-orange-500 text-white" : "border border-slate-300 hover:bg-slate-50"}`} title="Show all boxes (orange = auto-detected, green = added) — edit / delete" data-testid="bln-boxes-toggle">
              <span className="inline-flex gap-0.5"><span className={`w-3 h-3.5 rounded-sm ${showBoxes ? "bg-white" : "bg-orange-500"}`} /><span className={`w-3 h-3.5 rounded-sm ${showBoxes ? "bg-white/70" : "bg-emerald-500"}`} /></span> Show
            </button>
            <button onClick={() => { setGreenMode((s) => !s); setShowBoxes(true); }} className={`inline-flex items-center gap-1 px-2 py-1.5 text-xs rounded mr-1 ${greenMode ? "bg-emerald-600 text-white" : "border border-slate-300 hover:bg-slate-50"}`} title="Add a box — drag over a dimension the app missed to add it (green)" data-testid="bln-green-toggle">
              <Plus size={14} /> Add box
            </button>
            {anyBoxMode && (
              <button onClick={saveAndSequence} disabled={renumbering} className="inline-flex items-center gap-1 px-2 py-1.5 text-xs rounded mr-1 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white" title="Save all boxes and number them clockwise from 1" data-testid="bln-save-seq">
                <Check size={14} /> {renumbering ? "Saving…" : "Save"}
              </button>
            )}
            <button onClick={() => setZoom((z) => Math.max(0.15, z - 0.1))} className="p-1.5 border border-slate-300 rounded hover:bg-slate-50"><ZoomOut size={15} /></button>
            <span className="text-xs w-12 text-center font-mono">{Math.round(zoom * 100)}%</span>
            <button onClick={() => setZoom((z) => Math.min(3, z + 0.1))} className="p-1.5 border border-slate-300 rounded hover:bg-slate-50"><ZoomIn size={15} /></button>
          </div>
        </div>
        {isKcDoc && (doc.warnings || []).length > 0 && (
          <div className="px-4 py-2 bg-amber-50 border-b border-amber-200 text-xs text-amber-900" data-testid="bln-kc-warnings">
            <div className="flex items-start gap-2">
              <AlertTriangle size={14} className="mt-0.5 shrink-0 text-amber-600" />
              <ul className="list-disc pl-4 space-y-0.5">
                {doc.warnings.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            </div>
          </div>
        )}
        {eyeMode && (
          <div className="flex flex-col gap-2 px-4 py-2 bg-indigo-50 border-b border-indigo-200 text-sm text-indigo-900" data-testid="bln-eye-bar">
            <div className="flex items-center gap-3 flex-wrap">
              <EyeOff size={15} />
              <span className="font-semibold">{balloons.filter((b) => (b.text || "").trim() || (b.bw && b.bh) || b.manual).length} detected hidden</span>
              <span className="text-indigo-700">· drag a box around any text still visible to read it</span>
              <div className="ml-auto flex items-center gap-2">
                <button onClick={finishEye} className="inline-flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 text-white text-xs rounded px-3 py-1" data-testid="bln-eye-finish"><Check size={13} /> Finish</button>
                <button onClick={cancelEye} className="inline-flex items-center gap-1 border border-indigo-300 text-indigo-700 hover:bg-indigo-100 text-xs rounded px-3 py-1" data-testid="bln-eye-cancel"><X size={13} /> Cancel (Esc)</button>
              </div>
            </div>
            {eyePending.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {eyePending.map((p) => (
                  <div key={p.id} className="flex items-center gap-1 bg-white border border-indigo-200 rounded px-2 py-1" data-testid={`bln-eye-pending-${p.id}`}>
                    {p.busy && <span className="text-[11px] text-indigo-500">reading…</span>}
                    <input value={p.text} onChange={(e) => setEyePending((prev) => prev.map((q) => q.id === p.id ? { ...q, text: e.target.value } : q))}
                      placeholder={p.error || "type value"} className="text-xs border border-slate-200 rounded px-1 py-0.5 w-32" />
                    {p.parsed?.type && <span className="text-[10px] text-slate-500">{p.parsed.type}</span>}
                    <button onClick={() => setEyePending((prev) => prev.filter((q) => q.id !== p.id))} className="text-red-500 hover:text-red-700" title="Discard"><X size={12} /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        {reorderMode && (
          <div className="flex items-center justify-center gap-3 px-4 py-2 bg-indigo-50 border-b border-indigo-200 text-sm text-indigo-800" data-testid="bln-reorder-banner">
            Re-ordering — click once inside each view/section in the order you want ({picked.length} set). Balloons number clockwise within each. <kbd className="px-1 border rounded bg-white">Esc</kbd> cancels.
            <button onClick={undoPick} disabled={!picked.length} className="ml-1 border border-indigo-300 text-indigo-700 disabled:opacity-40 text-xs rounded px-2 py-1">Undo</button>
            <button onClick={applyReorder} disabled={!picked.length || applying} className="bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-xs rounded px-3 py-1">{applying ? "Applying…" : "Apply order"}</button>
          </div>
        )}
        <div className="flex-1 overflow-auto p-6 flex items-start justify-center">
          {page?.image_url ? (
            <div className="relative flex items-center justify-center flex-shrink-0"
              style={{ width: `${(rotation % 180 ? (page.height || 600) : (page.width || 800)) * zoom}px`, height: `${(rotation % 180 ? (page.width || 800) : (page.height || 600)) * zoom}px` }}>
            <div ref={imgWrapRef} onClick={onImageClick}
              onMouseDown={onStageMouseDown} onMouseMove={onStageMouseMove} onMouseUp={onStageMouseUp}
              className="relative shadow-lg bg-white cursor-crosshair"
              style={{ width: `${(page.width || 800) * zoom}px`, transform: `rotate(${rotation}deg)`, transformOrigin: "center center" }}
              title={regionMode ? `Drag a box to add a ${regionMode}` : reorderMode ? "Click once in each view/section, in order" : desigMode ? "Drag a box over the dimensions to designate" : redactMode ? "Drag a box to black-out an area" : addNoteMode ? "Click to add a note balloon" : addDimMode ? "Click to add a dimension balloon" : "Click to add a balloon"}>
              <img src={page.image_url} alt={doc.name} className="w-full block select-none" draggable={false} />
              {/* View / table regions — editable; shown when All-views is on, on the Views tab, or selected */}
              {views.filter((v) => (v.page || 1) === (page.page || 1) && (showAllViews || selectedView === v.id)).map((v) => {
                const isTable = (v.kind || "view") === "table";
                const active = selectedView === v.id;
                const col = isTable ? "59,130,246" : "16,185,129";   // table=blue, view=green
                return (
                  <div key={v.id} onMouseDown={(e) => onViewMouseDown(e, v, "move")} onClick={(e) => { e.stopPropagation(); setSelectedView(v.id); }}
                    className="absolute cursor-move group" style={{ left: `${v.x * 100}%`, top: `${v.y * 100}%`, width: `${v.w * 100}%`, height: `${v.h * 100}%`, border: `2px solid rgb(${col})`, background: `rgba(${col},${active ? 0.14 : 0.05})` }}>
                    <span className="absolute -top-5 left-0 text-[10px] font-semibold px-1 rounded" style={{ background: `rgb(${col})`, color: "#fff" }}>{v.name}</span>
                    <button onMouseDown={(e) => e.stopPropagation()} onClick={(e) => { e.stopPropagation(); removeView(v.id); }}
                      title="Delete view" className="absolute -top-2 -right-2 bg-white text-red-600 rounded-full ring-1 ring-red-200 shadow p-0.5 opacity-0 group-hover:opacity-100"><X size={11} /></button>
                    <div onMouseDown={(e) => onViewMouseDown(e, v, "resize")} title="Resize"
                      className="absolute -bottom-1.5 -right-1.5 w-3 h-3 rounded-sm cursor-se-resize opacity-0 group-hover:opacity-100" style={{ background: `rgb(${col})` }} />
                  </div>
                );
              })}
              {/* Redactions (blacked-out areas) */}
              {redactions.filter((r) => (r.page || 1) === (page.page || 1)).map((r, idx) => (
                <div key={`red-${idx}`} className={`absolute bg-black ${redactMode ? "ring-2 ring-orange-400" : "pointer-events-none"}`}
                  style={{ left: `${r.x * 100}%`, top: `${r.y * 100}%`, width: `${r.w * 100}%`, height: `${r.h * 100}%` }}>
                  {redactMode && (
                    <button onClick={(e) => { e.stopPropagation(); removeRedaction(idx); }}
                      onMouseDown={(e) => e.stopPropagation()}
                      className="absolute -top-2 -right-2 bg-white text-red-600 rounded-full shadow ring-1 ring-red-200 p-0.5" title="Remove redaction">
                      <X size={12} />
                    </button>
                  )}
                </div>
              ))}
              {/* Hide-numbers mode: outline the detected dimensions (orange), values stay visible.
                  A callout with NO box is one still left to balloon — click it to add one. */}
              {anyBoxMode && balloons.filter((b) => (b.page || 1) === (page.page || 1) &&
                ((b.text || "").trim() || (b.bw && b.bh) || b.manual)).map((b) => {
                const bw = b.bw || autoBoxW(b);
                const bh = b.bh || 0.022;
                const boxPxW = bw * (page.width || 800) * zoom;
                const boxPxH = bh * (page.height || 600) * zoom;
                const hs = Math.max(7, Math.min(18, Math.min(boxPxW, boxPxH) * 0.55));   // handle size ∝ box
                const col = b.manual ? "emerald" : "orange";
                return (
                  <div key={`bx-${b.n}`}
                    onMouseDown={(e) => onBoxMouseDown(e, b, "move")}
                    onClick={(e) => e.stopPropagation()}
                    title={b.text}
                    className={`absolute border-2 bg-transparent rounded-[2px] group ${seqMode ? "pointer-events-none" : "cursor-move"} ${b.manual ? "border-emerald-500 hover:bg-emerald-400/10" : "border-orange-500 hover:bg-orange-400/10"}`}
                    style={{ left: `${(b.x - bw / 2) * 100}%`, top: `${(b.y - bh / 2) * 100}%`, width: `${bw * 100}%`, height: `${bh * 100}%` }}>
                    <button onMouseDown={(e) => e.stopPropagation()} onClick={(e) => { e.stopPropagation(); removeBalloon(b.n); }}
                      title="Delete" className="absolute bg-white text-red-600 rounded-full ring-1 ring-red-200 shadow flex items-center justify-center opacity-0 group-hover:opacity-100"
                      style={{ width: `${hs + 4}px`, height: `${hs + 4}px`, top: `-${(hs + 4) / 2}px`, right: `-${(hs + 4) / 2}px` }}>
                      <X size={Math.max(9, hs)} />
                    </button>
                    <div onMouseDown={(e) => onBoxMouseDown(e, b, "resize")} title="Resize"
                      className={`absolute border-2 border-white rounded-sm cursor-se-resize shadow ${b.manual ? "bg-emerald-500" : "bg-orange-500"}`}
                      style={{ width: `${hs}px`, height: `${hs}px`, bottom: `-${hs / 2}px`, right: `-${hs / 2}px` }} />
                  </div>
                );
              })}
              {/* EYE mode — white-mask every detected item so only UNDETECTED text stays visible. */}
              {eyeMode && balloons.filter((b) => (b.page || 1) === (page.page || 1) &&
                ((b.text || "").trim() || (b.bw && b.bh) || b.manual)).map((b) => {
                const bw = (b.bw || autoBoxW(b)) * 1.12;
                const bh = (b.bh || 0.026) * 1.3;
                return (
                  <div key={`mask-${b.n}`} className="absolute bg-white pointer-events-none"
                    style={{ left: `${(b.x - bw / 2) * 100}%`, top: `${(b.y - bh / 2) * 100}%`, width: `${bw * 100}%`, height: `${bh * 100}%` }} />
                );
              })}
              {/* EYE mode — pending read boxes (indigo) the user drew over missed text. */}
              {eyeMode && eyePending.map((p) => (
                <div key={`pend-${p.id}`} className="absolute border-2 border-indigo-500 bg-indigo-400/10 pointer-events-none"
                  style={{ left: `${p.bbox.x * 100}%`, top: `${p.bbox.y * 100}%`, width: `${p.bbox.w * 100}%`, height: `${p.bbox.h * 100}%` }} />
              ))}
              {/* Leader lines for moved balloons */}
              {!eyeMode && (
                <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none">
                  {!anyBoxMode && balloons.filter((b) => (b.page || 1) === (page.page || 1) && (showNumbers || !(b.text || "").trim())).map((b) => {
                    const c = circlePos(b);
                    const col = b.designator ? "#dc2626" : b.verified ? "#059669" : "#2563eb";
                    // Bent (elbow) leader: from circle straight down/up to the anchor's level, then across.
                    const elbowX = c.x, elbowY = b.y;
                    return (
                      <polyline key={`ldr-${b.n}`} points={`${c.x * 100},${c.y * 100} ${elbowX * 100},${elbowY * 100} ${b.x * 100},${b.y * 100}`}
                        fill="none" stroke={col} strokeWidth="1" vectorEffect="non-scaling-stroke" />
                    );
                  })}
                </svg>
              )}
              {/* Sequence connector (Re-order dimensions mode) */}
              {seqMode && seqOrder.length > 1 && (
                <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none">
                  <polyline points={seqOrder.map((n) => { const b = balloons.find((x) => x.n === n); return b ? `${b.x * 100},${b.y * 100}` : ""; }).filter(Boolean).join(" ")}
                    fill="none" stroke="#f97316" strokeWidth="2" strokeDasharray="4 3" vectorEffect="non-scaling-stroke" />
                </svg>
              )}
              {!eyeMode && (!anyBoxMode || seqMode) && balloons.filter((b) => (b.page || 1) === (page.page || 1) && (showNumbers || seqMode || !(b.text || "").trim())).map((b) => {
                const seqIdx = seqMode ? seqOrder.indexOf(b.n) : -1;
                const selected = seqIdx >= 0;
                const level = b.designator ? b.designator.toLowerCase() : "regular";
                const lv = settings.levels[level] || settings.levels.regular;
                const shp = level === "regular" ? (lv.shape || "circle") : (LEVEL_SHAPE[level] || "square");
                const ss = SHAPE_STYLE[shp] || SHAPE_STYLE.circle;
                const fill = selected ? "#f97316" : (lv.color || "#2563eb");
                const canDetail = !seqMode && !desigMode && !redactMode && !reorderMode;
                const active = b.n === detailFor;
                const c = circlePos(b);
                const dim = Math.max(16, 22 * zoom) * settings.size;
                const ringCls = active ? "ring-4 ring-sky-300" : b.verified ? "ring-2 ring-emerald-400" : "ring-2 ring-white";
                return (
                  <div key={b.n}
                    onMouseDown={(e) => onBalloonMouseDown(e, b)}
                    onClick={(e) => { e.stopPropagation(); if (seqMode) pickSeq(b.n); }}
                    className={`absolute -translate-x-1/2 -translate-y-1/2 text-white font-bold flex items-center justify-center shadow leading-none ${seqMode ? "cursor-pointer" : canDetail ? "cursor-move" : ""} ${ringCls} ${seqMode && !selected ? "opacity-50" : ""}`}
                    style={{ left: `${c.x * 100}%`, top: `${c.y * 100}%`, width: `${dim}px`, height: `${dim}px`, backgroundColor: fill, ...ss.box }}
                    title={b.designator ? `${b.designator} — ${b.text}` : (b.text || "Drag to move · click to edit")}>
                    <span style={{ ...ss.num, fontSize: `${Math.max(8, dim * (String(selected ? seqIdx + 1 : b.n).length >= 2 ? 0.42 : 0.52))}px` }}>{selected ? seqIdx + 1 : b.n}</span>
                  </div>
                );
              })}
              {/* Draggable leader tip — only for the selected balloon (click a balloon first) */}
              {showNumbers && !seqMode && !desigMode && !redactMode && !reorderMode && balloons.filter((b) => (b.page || 1) === (page.page || 1) && b.n === detailFor).map((b) => (
                <div key={`anc-${b.n}`}
                  onMouseDown={(e) => onBalloonMouseDown(e, b, "anchor")}
                  onClick={(e) => e.stopPropagation()}
                  title="Drag the leader tip"
                  className="absolute -translate-x-1/2 -translate-y-1/2 rounded-full bg-white cursor-move"
                  style={{ left: `${b.x * 100}%`, top: `${b.y * 100}%`, width: `${Math.max(7, 9 * zoom)}px`, height: `${Math.max(7, 9 * zoom)}px`, border: `2px solid ${b.designator ? "#dc2626" : b.verified ? "#059669" : "#2563eb"}` }} />
              ))}
              {/* Quick-delete ✕ on the selected balloon */}
              {showNumbers && detailBalloon && (detailBalloon.page || 1) === (page.page || 1) && (
                <button
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => { e.stopPropagation(); removeBalloon(detailBalloon.n); setDetailFor(null); }}
                  title="Delete balloon (Del)"
                  className="absolute z-30 rounded-full bg-red-600 hover:bg-red-700 text-white flex items-center justify-center ring-2 ring-white shadow"
                  style={{ left: `${circlePos(detailBalloon).x * 100}%`, top: `${circlePos(detailBalloon).y * 100}%`, width: `${Math.max(14, 15 * zoom)}px`, height: `${Math.max(14, 15 * zoom)}px`, transform: `translate(${Math.max(16, 22 * zoom) * settings.size * 0.4}px, -${Math.max(16, 22 * zoom) * settings.size * 1.0}px)` }}>
                  <X size={11} />
                </button>
              )}
              {/* Designator drag-box + popup */}
              {(desigMode || redactMode || regionMode || greenMode || eyeMode) && dragRect && (
                <div className={`absolute border-2 border-dashed pointer-events-none ${redactMode ? "border-slate-700 bg-black/50" : eyeMode ? "border-indigo-600 bg-indigo-400/20" : greenMode ? "border-emerald-500 bg-emerald-400/20" : regionMode === "table" ? "border-blue-500 bg-blue-400/20" : regionMode === "view" ? "border-emerald-500 bg-emerald-400/20" : "border-orange-500 bg-orange-400/20"}`}
                  style={{ left: `${dragRect.x * 100}%`, top: `${dragRect.y * 100}%`, width: `${dragRect.w * 100}%`, height: `${dragRect.h * 100}%` }} />
              )}
              {desig && (
                <div className="absolute z-30 bg-white rounded-lg shadow-xl border border-purple-300 p-3 w-48"
                  style={{ left: `${Math.min(desig.rect.x + desig.rect.w, 0.82) * 100}%`, top: `${desig.rect.y * 100}%` }}
                  onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()} onMouseUp={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold text-slate-800">Ch. Designator</span>
                    <button onClick={() => setDesig(null)} className="text-slate-400 hover:text-slate-600"><X size={15} /></button>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <select value={desig.type} onChange={(e) => changeDesig(e.target.value)}
                      className="flex-1 border border-slate-300 rounded px-2 py-1.5 text-sm">
                      {DESIG_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                    <button onClick={clearDesig} className="p-1.5 text-red-500 hover:bg-red-50 rounded" title="Remove designator"><Trash2 size={15} /></button>
                  </div>
                  <div className="text-[11px] text-slate-400 mt-1.5">{desig.ids.length} dimension{desig.ids.length > 1 ? "s" : ""} marked</div>
                </div>
              )}
              {/* Clicked section-order points (re-order mode) */}
              {reorderMode && picked.map((p, i) => (
                <div key={i} className="absolute -translate-x-1/2 -translate-y-1/2 rounded-full bg-indigo-600 text-white text-xs font-bold flex items-center justify-center shadow-lg ring-2 ring-white pointer-events-none"
                  style={{ left: `${p.x * 100}%`, top: `${p.y * 100}%`, width: `${Math.max(24, 32 * zoom)}px`, height: `${Math.max(24, 32 * zoom)}px` }}>
                  {i + 1}
                </div>
              ))}
            </div>
            </div>
          ) : (
            <div className="text-sm text-slate-400 mt-20">No preview available. <a href={doc.file_url} target="_blank" rel="noreferrer" className="text-orange-600 underline">Open original</a></div>
          )}
        </div>

        {/* Re-order dimensions header (click balloons in sequence) */}
        {seqMode && (
          <div className="absolute top-3 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2 bg-slate-900 text-white rounded-xl px-3 py-2 shadow-xl" data-testid="bln-seq-header">
            <span className="text-xs pr-1">Click dimensions in the order you want ({seqOrder.length}/{balloons.length})</span>
            <span className="inline-flex items-center gap-1 bg-blue-600 rounded-lg px-2 py-1 text-xs font-bold" title="The next click becomes this number">Next: {seqOrder.length + (settings.startNumber || 1)}</span>
            <button onClick={applySequence} disabled={!seqOrder.length || seqApplying} className="inline-flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 rounded-lg px-3 py-1.5 text-xs font-semibold">
              <Check size={13} /> {seqApplying ? "Finishing…" : "Finish"}
            </button>
            <button onClick={() => { setSeqMode(false); setSeqOrder([]); }} className="inline-flex items-center gap-1 bg-slate-700 hover:bg-slate-600 rounded-lg px-3 py-1.5 text-xs">
              <X size={13} /> Cancel
            </button>
          </div>
        )}

        {/* Details editor panel (click a balloon) */}
        {detailBalloon && (
          <div className="absolute top-3 left-3 z-40 w-52 max-h-[82vh] flex flex-col bg-white rounded-xl shadow-2xl border border-slate-200 text-[13px]" data-testid="bln-details">
            <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-100 shrink-0">
              <span className="text-sm font-semibold text-slate-800">Details</span>
              <input type="number" min="1" title="Balloon number — change to re-sequence"
                value={numDraft != null ? numDraft : detailBalloon.n}
                onChange={(e) => setNumDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commitNumDraft(); } }}
                onBlur={commitNumDraft}
                className={`h-6 w-9 text-center text-white text-xs font-bold outline-none ${detailBalloon.designator ? "rounded-sm bg-red-600" : "rounded-full bg-blue-600"}`} />
              <button onClick={() => setDetailFor(null)} className="ml-auto text-slate-400 hover:text-slate-600"><X size={16} /></button>
            </div>
            <div className="p-2.5 space-y-1.5 overflow-y-auto">
              <TypeDropdown value={detailBalloon.dim_type || "Custom"} onChange={changeType} />
              <button onClick={async () => {
                const t = await readAt(detailBalloon.x, detailBalloon.y);
                if (t) patchDetail((detailBalloon.dim_type || "") === "Note" ? { note: t } : { base: t });
                else toast("Couldn't read a value there — type it in.");
              }} className="w-full inline-flex items-center justify-center gap-1.5 border border-slate-300 text-slate-600 hover:bg-slate-50 text-[11px] font-medium rounded-md py-1">
                <RefreshCw size={12} /> Read value from drawing
              </button>
              {/* Quick GD&T symbol picker */}
              <div>
                <div className="text-[11px] text-slate-500 mb-0.5">GD&amp;T symbol</div>
                <div className="flex flex-wrap gap-0.5">
                  {GDT_QUICK.map(([sym, label]) => (
                    <button key={label} title={label} onClick={() => changeType(label, "gdt")}
                      className={`w-6 h-6 border rounded text-xs leading-none flex items-center justify-center hover:bg-slate-50 ${detailBalloon.dim_type === label ? "border-orange-400 bg-orange-50 text-orange-600" : "border-slate-300 text-slate-700"}`}>
                      {sym}
                    </button>
                  ))}
                </div>
              </div>
              {(detailBalloon.dim_type || "") === "Note" ? (
                <textarea value={detailBalloon.note || ""} onChange={(e) => patchDetail({ note: e.target.value })}
                  placeholder="Enter note text…" rows={3}
                  className="w-full border border-slate-300 rounded-md px-2 py-1.5 text-xs resize-none" />
              ) : (
                <>
                  {/* Base value with +/- tolerance steppers (pre6 layout) */}
                  <div className="flex items-stretch border border-slate-300 rounded-md overflow-hidden">
                    <input value={detailBalloon.base || ""} onChange={(e) => patchDetail({ base: e.target.value })}
                      placeholder="Base value" className="flex-1 min-w-0 px-2 py-1.5 text-sm font-semibold font-mono outline-none" />
                    <div className="flex flex-col border-l border-slate-200 divide-y divide-slate-200">
                      <div className="flex items-center gap-1 px-1.5 py-0.5">
                        <span className="text-slate-500 font-bold w-2.5 text-center">+</span>
                        <input value={detailBalloon.tmax || ""} onChange={(e) => patchDetail({ tmax: e.target.value })}
                          placeholder="0" className="w-9 text-xs font-mono text-blue-700 outline-none" />
                      </div>
                      <div className="flex items-center gap-1 px-1.5 py-0.5">
                        <span className="text-slate-500 font-bold w-2.5 text-center">−</span>
                        <input value={detailBalloon.tmin || ""} onChange={(e) => patchDetail({ tmin: e.target.value })}
                          placeholder="0" className="w-9 text-xs font-mono text-blue-700 outline-none" />
                      </div>
                    </div>
                  </div>
                  <label className="flex items-center justify-end gap-1.5 text-xs text-slate-600">
                    <input type="checkbox" checked={!!detailBalloon.basic} onChange={(e) => patchDetail({ basic: e.target.checked })} /> BASIC
                  </label>
                  <div className="flex gap-2 items-end pt-1 border-t border-slate-100">
                    <div className="flex-1">
                      <label className="text-[11px] text-slate-500">Class</label>
                      <input value={detailBalloon.dclass || ""} onChange={(e) => patchDetail({ dclass: e.target.value })}
                        placeholder="e.g: g6" className="w-full border border-slate-300 rounded-md px-1.5 py-1 text-xs font-mono" />
                    </div>
                    <div className="w-16">
                      <label className="text-[11px] text-slate-500">Count</label>
                      <input value={detailBalloon.count || ""} onChange={(e) => patchDetail({ count: e.target.value })}
                        placeholder="1" className="w-full border border-slate-300 rounded-md px-1.5 py-1 text-xs font-mono" />
                    </div>
                  </div>
                </>
              )}
              <div className="flex items-center justify-between pt-1 border-t border-slate-100">
                <span className="inline-flex items-center gap-1.5 text-sm text-orange-600 font-medium">
                  <span className="capitalize">{detailBalloon.kind || "dimension"}</span>
                </span>
                <button onClick={() => { removeBalloon(detailBalloon.n); setDetailFor(null); }} className="p-1.5 text-slate-400 hover:text-red-600" title="Delete balloon"><Trash2 size={16} /></button>
              </div>
              <label className="flex items-center gap-2 text-xs text-slate-700 pt-1 border-t border-slate-100">
                <input type="checkbox" checked={!!detailBalloon.verified} onChange={() => toggleVerify(detailBalloon.n)} /> Verified
              </label>
              {dirty && <button onClick={save} disabled={saving} className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-xs font-semibold rounded-md py-1.5">{saving ? "Saving…" : "Save"}</button>}
            </div>
          </div>
        )}

        {/* Floating tool dock (pre6-style) — draggable via the grip */}
        {page?.image_url && (
          <div ref={barRef}
            className={`absolute z-30 flex items-center gap-1 bg-slate-900/95 text-white rounded-xl px-2 py-1.5 shadow-xl ${toolPos ? "" : "bottom-4 left-1/2 -translate-x-1/2"}`}
            style={toolPos ? { left: `${toolPos.x}px`, top: `${toolPos.y}px` } : undefined}
            data-testid="bln-toolbar">
            <button onMouseDown={onGripDown} className="cursor-move px-0.5 text-slate-400 hover:text-white" title="Drag to move"><GripVertical size={16} /></button>
            <span className="w-px h-5 bg-white/20 mx-0.5" />
            <ToolBtn active={addDimMode} onClick={() => { setAddDimMode((m) => !m); setAddNoteMode(false); setReorderMode(false); setSeqMode(false); setDesigMode(false); setRedactMode(false); }} title="Add Dimension — click on the drawing to place a dimension, then fill in Details (A)"><Ruler size={16} /></ToolBtn>
            <ToolBtn active={addNoteMode} onClick={() => { setAddNoteMode((m) => !m); setAddDimMode(false); setReorderMode(false); setSeqMode(false); setDesigMode(false); setRedactMode(false); }} title="Add Note — click on the drawing to place a note balloon (N)"><FileText size={16} /></ToolBtn>
            <ToolBtn active={redactMode} onClick={() => { setRedactMode((m) => !m); setDragRect(null); setDesigMode(false); setDesig(null); setReorderMode(false); setSeqMode(false); setAddNoteMode(false); }} title="Redact an area — drag a box to black it out (R)"><Ban size={16} /></ToolBtn>
            <ToolBtn active={desigMode} onClick={() => { setDesigMode((m) => !m); setDesig(null); setDragRect(null); setReorderMode(false); setSeqMode(false); setAddNoteMode(false); setRedactMode(false); }} title="Add Ch. Designator Symbol — drag a box to mark critical characteristics"><Contrast size={16} /></ToolBtn>
            <span className="w-px h-5 bg-white/20 mx-0.5" />
            <ToolBtn active={reorderMode} onClick={() => { setReorderMode((m) => !m); setPicked([]); setAddNoteMode(false); setSeqMode(false); }} title="Reorder balloons by view — click each view in order (V)"><ListOrdered size={16} /></ToolBtn>
            <ToolBtn active={seqMode} onClick={() => { setSeqMode((m) => !m); setSeqOrder([]); setReorderMode(false); setAddNoteMode(false); }} title="Re-order dimensions — click balloons one-by-one in the sequence you want (O)"><Share2 size={16} /></ToolBtn>
            <ToolBtn onClick={() => renumberClockwise()} disabled={renumbering} title="Auto-number clockwise"><RefreshCw size={16} className={renumbering ? "animate-spin" : ""} /></ToolBtn>
            <ToolBtn onClick={() => setRotation((r) => (r + 90) % 360)} title="Rotate the drawing 90°"><RotateCw size={16} /></ToolBtn>
            <span className="w-px h-5 bg-white/20 mx-0.5" />
            <ToolBtn active={eyeMode} onClick={toggleEye}
              title="Hide detected dimensions — white-mask them and box any missed text (H)">
              {eyeMode ? <EyeOff size={16} /> : <Eye size={16} />}
            </ToolBtn>
          </div>
        )}
      </div>

      {/* Right panel */}
      <div className="w-80 shrink-0 border-l border-slate-200 bg-white flex flex-col">
        {/* Mode dropdown + Settings gear */}
        <div className="p-3 pb-0 flex items-center gap-2">
          <div className="relative flex-1">
            <button onClick={() => setModeOpen((o) => !o)} className="w-full flex items-center justify-between border border-slate-300 rounded-md px-3 py-2 text-sm font-medium bg-white hover:bg-slate-50" data-testid="bln-mode">
              <span className="inline-flex items-center gap-2">{MODES.find((m) => m.id === mode)?.icon} {MODES.find((m) => m.id === mode)?.label}</span>
              <ChevronDown size={15} className="text-slate-400" />
            </button>
            {modeOpen && (
              <div className="absolute z-40 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-xl py-1">
                {MODES.map((m) => (
                  <button key={m.id} onClick={() => { if (m.id === "ballooning") setMode(m.id); else toast(`${m.label} — coming soon`); setModeOpen(false); }}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-slate-50 ${mode === m.id ? "text-orange-600 font-semibold" : "text-slate-700"}`}>
                    {m.icon} {m.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button onClick={() => setSettingsOpen(true)} className="p-2 border border-slate-300 rounded-md text-slate-600 hover:bg-slate-50" title="Settings" data-testid="bln-settings-btn"><SettingsIcon size={16} /></button>
        </div>
        <div className="p-3 space-y-2 border-b border-slate-100">
          <button onClick={downloadPdf} className="w-full inline-flex items-center justify-center gap-1.5 bg-orange-600 hover:bg-orange-700 text-white text-sm font-semibold rounded-md px-3 py-2" data-testid="bln-download-pdf">
            <Download size={15} /> Download PDF
          </button>
          <button onClick={downloadInspection} className="w-full inline-flex items-center justify-center gap-1.5 border border-slate-300 text-slate-700 hover:bg-slate-50 text-sm font-semibold rounded-md px-3 py-2" data-testid="bln-inspection">
            <FileSpreadsheet size={15} /> Inspection Sheet
          </button>
        </div>

        <div className="flex items-center gap-4 px-3 pt-3 text-sm border-b border-slate-100">
          <button onClick={() => setTab("dims")} className={`pb-2 font-semibold ${tab === "dims" ? "text-orange-600 border-b-2 border-orange-500" : "text-slate-500"}`}>Dims. {balloons.length}</button>
          <button onClick={() => setTab("views")} className={`pb-2 font-semibold inline-flex items-center gap-1 ${tab === "views" ? "text-orange-600 border-b-2 border-orange-500" : "text-slate-500"}`}>Views {views.length > 0 && <span className="text-[10px] bg-slate-200 text-slate-600 rounded-full px-1.5">{views.length}</span>}</button>
          <button onClick={() => setTab("tols")} className={`pb-2 font-semibold ${tab === "tols" ? "text-orange-600 border-b-2 border-orange-500" : "text-slate-500"}`}>Tols.</button>
          {dirty && <button onClick={save} disabled={saving} className="ml-auto text-xs bg-emerald-600 hover:bg-emerald-700 text-white rounded px-2 py-1 mb-1" data-testid="bln-save">{saving ? "Saving…" : "Save"}</button>}
        </div>

        {doc.auto_status === "ocr" && (
          <div className="mx-3 mt-2 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
            Auto-read via OCR (no text layer) — please verify each value.
          </div>
        )}
        {tab !== "views" && (
          <div className="px-3 py-2 border-b border-slate-100">
            <div className="flex items-center justify-between text-[11px] text-slate-500 mb-1">
              <span>{verified}/{balloons.length} verified</span>
              <button onClick={() => addBalloonAt(0.5, 0.5)} className="inline-flex items-center gap-0.5 text-orange-600 hover:underline"><Plus size={11} /> Add</button>
            </div>
            <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
              <div className="h-full bg-emerald-500" style={{ width: `${balloons.length ? (verified / balloons.length) * 100 : 0}%` }} />
            </div>
          </div>
        )}

        {tab === "views" ? (
          <div className="flex-1 overflow-y-auto p-3 space-y-2">
            <div className="flex items-center justify-between text-xs text-slate-500">
              <span>A <b>View</b> = a picture of the part. Draw a box around each view.</span>
              <label className="inline-flex items-center gap-1 whitespace-nowrap">
                <input type="checkbox" checked={showAllViews} onChange={(e) => setShowAllViews(e.target.checked)} /> All views
              </label>
            </div>
            <div className="flex gap-2">
              <button onClick={() => { setRegionMode("view"); setTab("views"); }} className={`flex-1 inline-flex items-center justify-center gap-1.5 text-sm font-semibold rounded-md py-2 ${regionMode === "view" ? "bg-emerald-600 text-white" : "bg-orange-600 hover:bg-orange-700 text-white"}`}>
                <Plus size={14} /> {regionMode === "view" ? "Draw the view…" : "Add View"}
              </button>
              <button onClick={() => { setRegionMode("table"); setTab("views"); }} className={`flex-1 inline-flex items-center justify-center gap-1.5 text-sm font-semibold rounded-md py-2 border ${regionMode === "table" ? "border-blue-500 text-blue-600 bg-blue-50" : "border-slate-300 text-slate-700 hover:bg-slate-50"}`}>
                <Table size={14} /> {regionMode === "table" ? "Draw the table…" : "Add Table"}
              </button>
            </div>
            {regionMode && <div className="text-[11px] text-slate-500 text-center">Drag a box on the drawing to mark the {regionMode}. <button onClick={() => setRegionMode(null)} className="underline">Cancel</button></div>}
            {views.filter((v) => (v.kind || "view") === "view").length > 0 && (
              <button onClick={numberByViews} disabled={numbering} className="w-full inline-flex items-center justify-center gap-1.5 text-sm font-semibold rounded-md py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white">
                <RefreshCw size={14} className={numbering ? "animate-spin" : ""} /> {numbering ? "Numbering…" : "Number by views"}
              </button>
            )}
            {views.length === 0 ? (
              <div className="text-center text-xs text-slate-400 py-8">No views yet. Use <b>Add View</b> to draw a region, e.g. Detail A / Detail B.</div>
            ) : (
              <div className="space-y-1.5">
                {views.map((v) => (
                  <div key={v.id} onClick={() => setSelectedView(v.id)}
                    className={`flex items-center gap-2 border rounded-lg px-3 py-2 cursor-pointer ${selectedView === v.id ? "border-emerald-400 bg-emerald-50" : "border-slate-200 hover:bg-slate-50"}`}>
                    <span className="w-3 h-3 rounded-sm shrink-0" style={{ background: (v.kind || "view") === "table" ? "#3b82f6" : "#10b981" }} />
                    <span className="flex-1 text-sm text-slate-700">{v.name}</span>
                    <button onClick={(e) => { e.stopPropagation(); removeView(v.id); }} className="text-slate-400 hover:text-red-600"><X size={15} /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
        <div className="flex-1 overflow-y-auto">
          {shown.length === 0 ? (
            <div className="p-6 text-center text-xs text-slate-400">
              {doc.auto_status === "image" ? "Image drawing — click the drawing to add balloons manually."
                : doc.auto_status === "no_text" ? "No dimension text found (scanned/flattened PDF). Click the drawing to balloon manually."
                : "No dimensions. Click the drawing to add balloons."}
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {shown.map((b, i) => (
                <li key={b.n} data-testid={`bln-dim-${b.n}`}
                  draggable={tab === "dims"}
                  onDragStart={() => setDragIdx(i)}
                  onDragOver={(e) => { if (tab === "dims") e.preventDefault(); }}
                  onDrop={() => { reorderDims(dragIdx, i); setDragIdx(null); }}
                  className={`flex items-center gap-2 px-3 py-2 ${tab === "dims" ? "cursor-grab" : ""} ${dragIdx === i ? "opacity-40" : ""}`}>
                  <span className={`h-5 w-5 shrink-0 text-white text-[10px] font-bold flex items-center justify-center ${b.designator ? "rounded-sm " + (DESIG_COLOR[b.designator] || "bg-red-600") : "rounded-full " + (b.verified ? "bg-emerald-600" : (KIND_COLOR[b.kind] || "bg-blue-600"))}`} title={b.designator || ""}>{b.n}</span>
                  {b.designator && <span className={`shrink-0 text-[9px] font-semibold border rounded px-1 py-0.5 ${DESIG_BADGE[b.designator] || "text-red-700 bg-red-50 border-red-200"}`} title={`${b.designator} characteristic`}>{b.designator}</span>}
                  {(b.kind === "gdt" || looksGdt(b.text)) ? (
                    <button onClick={() => setDetailFor(b.n)} title="GD&T — click to set the symbol/value" className="flex-1 min-w-0 flex items-center py-1 overflow-x-auto">
                      <GdtFrame symbol={gdtSymbol(b) || guessGdtSymbol(b.text)} text={b.text} />
                    </button>
                  ) : (
                    <input value={b.text} onChange={(e) => setText(b.n, e.target.value)} placeholder="dimension…"
                      className="flex-1 min-w-0 border border-transparent hover:border-slate-200 focus:border-slate-300 rounded px-1.5 py-1 text-xs font-mono" />
                  )}
                  {isKcDoc && b.kc?.Check && !b.verified && (
                    <span title={b.kc.Check} data-testid={`bln-check-${b.n}`} className="shrink-0 text-amber-500 cursor-help"><AlertTriangle size={14} /></span>
                  )}
                  <button onClick={() => toggleVerify(b.n)} className={`p-1 rounded ${b.verified ? "text-emerald-600" : "text-slate-300 hover:text-emerald-600"}`} title="Verify"><Check size={15} /></button>
                  <button onClick={() => removeBalloon(b.n)} className="p-1 text-slate-300 hover:text-red-600" title="Remove"><Trash2 size={14} /></button>
                </li>
              ))}
            </ul>
          )}
        </div>
        )}
      </div>

      {/* Settings modal */}
      {settingsOpen && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-14 bg-black/20" onClick={() => setSettingsOpen(false)}>
          <div className="w-[520px] max-h-[82vh] bg-white rounded-xl shadow-2xl flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="bln-settings">
            <div className="flex items-center gap-2 px-5 py-3 border-b border-slate-100">
              <SettingsIcon size={18} className="text-slate-500" />
              <span className="font-semibold text-lg text-slate-800">Settings</span>
              <button className="ml-auto text-slate-400 hover:text-slate-600" onClick={() => setSettingsOpen(false)}><X size={18} /></button>
            </div>
            <div className="flex gap-5 px-5 border-b border-slate-100 text-sm">
              {[["balloons", "Balloons"], ["pdf", "PDF Export"], ["plan", "Plan Export"]].map(([id, label]) => (
                <button key={id} onClick={() => setSettingsTab(id)} className={`py-2.5 font-semibold ${settingsTab === id ? "text-orange-600 border-b-2 border-orange-500" : "text-slate-500"}`}>{label}</button>
              ))}
            </div>
            <div className="p-5 overflow-y-auto space-y-5">
              {settingsTab === "balloons" ? (
                <>
                  {/* Per-level shape + colour */}
                  <div className="space-y-2">
                    {[["regular", "Regular"], ["minor", "Minor"], ["major", "Major"], ["critical", "Critical"]].map(([lvl, label]) => {
                      const cur = settings.levels[lvl] || {};
                      const shp = lvl === "regular" ? (cur.shape || "circle") : LEVEL_SHAPE[lvl];
                      const Swatch = ({ shape, active, onClick }) => (
                        <button onClick={onClick} className={`w-7 h-7 flex items-center justify-center rounded ${active ? "ring-2 ring-sky-400" : ""}`}>
                          <span className="w-4 h-4" style={{ backgroundColor: cur.color || "#64748b", ...SHAPE_STYLE[shape].box }} />
                        </button>
                      );
                      return (
                        <div key={lvl} className="flex items-center gap-3 border border-slate-200 rounded-lg p-2.5">
                          <span className="w-16 text-sm font-medium text-slate-700">{label}</span>
                          {lvl === "regular" ? (
                            <div className="inline-flex gap-1">
                              <Swatch shape="circle" active={shp === "circle"} onClick={() => setLevel("regular", { shape: "circle" })} />
                              <Swatch shape="pin" active={shp === "pin"} onClick={() => setLevel("regular", { shape: "pin" })} />
                            </div>
                          ) : (
                            <span className="w-7 h-7 flex items-center justify-center"><span className="w-4 h-4" style={{ backgroundColor: cur.color, ...SHAPE_STYLE[shp].box }} /></span>
                          )}
                          <div className="flex items-center gap-1.5 ml-auto">
                            {PALETTE.map((col) => (
                              <button key={col} onClick={() => setLevel(lvl, { color: col })}
                                className={`w-6 h-6 rounded ${cur.color === col ? "ring-2 ring-sky-400 ring-offset-1" : ""}`} style={{ backgroundColor: col }} />
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1"><label className="text-sm font-medium text-slate-700">Balloon size</label><span className="text-xs text-slate-400 font-mono">{settings.size.toFixed(2)}×</span></div>
                    <input type="range" min="0.6" max="1.8" step="0.05" value={settings.size} onChange={(e) => setSetting("size", parseFloat(e.target.value))} className="w-full" />
                  </div>
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-slate-700">Start number</label>
                    <input type="number" min="0" value={settings.startNumber} onChange={(e) => setSetting("startNumber", Math.max(0, parseInt(e.target.value) || 0))} className="w-24 border border-slate-300 rounded-md px-2 py-1.5 text-sm" />
                  </div>
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-slate-700">Numbering direction</label>
                    <div className="inline-flex rounded-md border border-slate-300 overflow-hidden">
                      <button onClick={() => setSetting("direction", "cw")} className={`px-3 py-1.5 text-sm inline-flex items-center gap-1 ${settings.direction === "cw" ? "bg-orange-500 text-white" : "bg-white text-slate-600"}`}><RotateCw size={14} /> CW</button>
                      <button onClick={() => setSetting("direction", "ccw")} className={`px-3 py-1.5 text-sm inline-flex items-center gap-1 ${settings.direction === "ccw" ? "bg-orange-500 text-white" : "bg-white text-slate-600"}`}><RotateCcw size={14} /> CCW</button>
                    </div>
                  </div>
                  <button onClick={() => { renumberClockwise(); setSettingsOpen(false); }} disabled={renumbering}
                    className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm font-semibold rounded-md py-2">
                    Apply numbering now
                  </button>
                  <p className="text-[11px] text-slate-400">Size updates live. Start number & direction apply when you re-number (button above, the ⟳ tool, or adding a balloon with auto re-number on).</p>
                </>
              ) : (
                <div className="text-center py-12 text-slate-400 text-sm">{settingsTab === "pdf" ? "PDF Export" : "Plan Export"} settings — coming soon.</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
